import React, { useMemo, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { buildScoringTree, flattenPoints, getOutcome, computeGameWinner, computeSetWinner } from '../../utils/scoringTree';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const PanelContainer = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows?.md || 'none'};
  font-family: ${theme.typography.fontFamily};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  min-height: 0;
`;

const PanelHeader = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.textSecondary};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const ToggleBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: 6px ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const ToggleLabel = styled.span`
  font-size: ${theme.typography.fontSizes.xs};
  color: ${theme.colors.textMuted};
  flex-shrink: 0;
  margin-right: 2px;
`;

const ToggleBtn = styled.button`
  flex: 1;
  padding: 3px 0;
  border: 1px solid ${(p) => p.$active ? theme.colors.primary : theme.colors.border};
  border-radius: 4px;
  background: ${(p) => p.$active ? `${theme.colors.primary}22` : 'transparent'};
  color: ${(p) => p.$active ? theme.colors.primary : theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.xs};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.1s;
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const TreeScroll = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: ${theme.spacing.xs} 0;
`;

const EmptyMsg = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.sm};
  text-align: center;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  padding: 0;
  font-family: ${theme.typography.fontFamily};
  transition: background 0.1s;
  &:hover { background: rgba(114, 210, 255, 0.07); }
`;

const SetRow = styled(Row)`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.primary};
`;

// GameRow is a div (not a button) because it contains interactive button children.
const GameRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  text-align: left;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  padding-left: 28px;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  transition: background 0.1s;
  &:hover { background: rgba(114, 210, 255, 0.07); }
`;

// PointRow is a div (not a button) because it contains interactive button children.
// Nesting <button> inside <button> is invalid HTML and causes misfired clicks.
const PointRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  text-align: left;
  padding: 3px ${theme.spacing.md};
  padding-left: 44px;
  gap: ${theme.spacing.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.sm};
  box-sizing: border-box;
  transition: background 0.1s;
  color: ${(p) => p.$isActive ? theme.colors.accent : theme.colors.textSecondary};
  font-weight: ${(p) =>
    p.$isActive ? theme.typography.fontWeights.semibold : theme.typography.fontWeights.normal};
  &:hover { color: ${theme.colors.text}; background: rgba(114, 210, 255, 0.07); }
`;

const Chevron = styled.span`
  font-size: 10px;
  color: ${theme.colors.textMuted};
  display: inline-block;
  transition: transform 0.15s;
  transform: ${(p) => (p.$open ? 'rotate(90deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${theme.colors.accent};
  flex-shrink: 0;
  visibility: ${(p) => (p.$visible ? 'visible' : 'hidden')};
`;

const WinnerBadge = styled.button`
  background: none;
  border: 1px solid ${(p) => p.$color};
  border-radius: 3px;
  color: ${(p) => p.$color};
  font-size: 10px;
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.semibold};
  line-height: 1;
  padding: 1px 4px;
  flex-shrink: 0;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  opacity: ${(p) => (p.disabled ? 0.35 : 1)};
  transition: background 0.1s;
  &:hover:not(:disabled) {
    background: ${(p) => `${p.$color}22`};
  }
`;

const EllipsisBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: 0 4px;
  font-size: 13px;
  line-height: 1;
  border-radius: 3px;
  flex-shrink: 0;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: ${theme.colors.text};
  }
`;

const PointMenu = styled.div`
  position: absolute;
  right: ${theme.spacing.md};
  top: 100%;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  z-index: 100;
  min-width: 170px;
  overflow: hidden;
`;

const PointMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 7px 12px;
  background: none;
  border: none;
  text-align: left;
  font-size: ${theme.typography.fontSizes.xs};
  font-family: ${theme.typography.fontFamily};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  color: ${(p) => (p.disabled ? theme.colors.textMuted : theme.colors.text)};
  opacity: ${(p) => (p.disabled ? 0.45 : 1)};
  &:hover:not(:disabled) {
    background: rgba(114, 210, 255, 0.1);
  }
`;

// ---------------------------------------------------------------------------
// ScoringTree — React.memo'd: only re-renders when active point changes,
// not on every video frame.
// ---------------------------------------------------------------------------

const ScoringTree = React.memo(({
  tree,
  activeStartTime,
  jumpTarget,
  onPointClick,
  onMergeWithPrevious,
  onAddPointAfter,
  onTogglePointWinner,
  onToggleGameWinner,
  onToggleSetWinner,
  onSplitGame,
  onMergeGameWithPrevious,
  expandedSets,
  expandedGames,
  toggleSet,
  toggleGame,
}) => {
  const [openMenuKey,     setOpenMenuKey]     = useState(null);
  const [openGameMenuKey, setOpenGameMenuKey] = useState(null);

  const setKey  = (s)    => s.setScore;
  const gameKey = (s, g) => `${s.setScore}::${g.gameScore}`;

  const fmtTime = (t) => {
    const mins = Math.floor(t / 60);
    const secs = (t % 60).toFixed(2).padStart(5, '0');
    return `${String(mins).padStart(2, '0')}:${secs}`;
  };

  return (
    <TreeScroll>
      {tree.map((set, si) => {
        const setOpen = !expandedSets.has(setKey(set));
        return (
          <div key={`set-${si}`}>
            <SetRow onClick={() => toggleSet(set)}>
              <Chevron $open={setOpen}>&#9654;</Chevron>
              <span>Set {si + 1}: {set.setScore}</span>
            </SetRow>

            {setOpen && set.games.map((game, gi) => {
              const gameOpen    = !expandedGames.has(gameKey(set, game));
              const nextGame    = set.games[gi + 1] ?? null;
              const nextSet     = tree[si + 1] ?? null;
              const gameMenuKey = `game-${si}-${gi}`;
              const gameMenuOpen = openGameMenuKey === gameMenuKey;
              return (
                <div key={`game-${si}-${gi}`} style={{ position: 'relative' }}>
                  <GameRow onClick={() => { setOpenGameMenuKey(null); toggleGame(set, game); }}>
                    <Chevron $open={gameOpen}>&#9654;</Chevron>
                    <span>Game {gi + 1}: {game.gameScore}</span>
                    <span style={{ flex: 1 }} />
                    <EllipsisBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenGameMenuKey(gameMenuOpen ? null : gameMenuKey);
                      }}
                      title="Game options"
                    >
                      ⋯
                    </EllipsisBtn>
                  </GameRow>
                  {gameMenuOpen && (
                    <PointMenu>
                      <PointMenuItem
                        disabled={gi === 0}
                        title={gi > 0 ? 'Merge all points of this game into the previous game' : 'No previous game to merge with'}
                        onClick={() => {
                          if (gi > 0 && onMergeGameWithPrevious) {
                            onMergeGameWithPrevious(game, set.games[gi - 1], set.games);
                          }
                          setOpenGameMenuKey(null);
                        }}
                      >
                        Merge into previous game
                      </PointMenuItem>
                    </PointMenu>
                  )}

                  {gameOpen && game.points.map((point, pi) => {
                    const isActive    = point.startTime === activeStartTime;
                    const jumpTime    = jumpTarget === 'start' ? point.startTime : point.endTime;
                    const ts          = fmtTime(jumpTime);
                    const menuKey     = `${si}-${gi}-${pi}`;
                    const menuOpen    = openMenuKey === menuKey;
                    const hasPrev     = pi > 0;
                    const hasNext     = pi < game.points.length - 1;
                    const isLastPoint = pi === game.points.length - 1;
                    const nextPoint   = isLastPoint ? null : game.points[pi + 1];
                    // canSplit: need ≥1 point after the clicked point to move to the new game
                    const canSplit    = !isLastPoint;

                    // Winner badge: last point uses game-score transition; others use point-score.
                    let winner, badgeDisabled, badgeTitle, handleBadgeClick;
                    if (isLastPoint) {
                      if (nextGame) {
                        // Determine winner from next game's game score (normal case).
                        winner        = computeGameWinner(game, nextGame);
                        badgeDisabled = !winner || !onToggleGameWinner;
                        badgeTitle    = winner
                          ? `${winner} won game — click to toggle`
                          : 'Cannot determine winner';
                        const toggled = winner === 'server' ? 'receiver' : 'server';
                        handleBadgeClick = (e) => {
                          e.stopPropagation();
                          if (winner && onToggleGameWinner) {
                            onToggleGameWinner(game, nextGame, toggled);
                          }
                        };
                      } else if (nextSet) {
                        // Last game of this set — infer winner from next set's set score.
                        const absWinner = computeSetWinner(set, nextSet);
                        winner = absWinner === null ? null
                               : absWinner === game.serverPlayer ? 'server' : 'receiver';
                        badgeDisabled = !winner || !onToggleSetWinner;
                        badgeTitle    = winner
                          ? `${winner} won set — click to toggle`
                          : 'Cannot determine (last set)';
                        const toggled = winner === 'server' ? 'receiver' : 'server';
                        handleBadgeClick = (e) => {
                          e.stopPropagation();
                          if (winner && onToggleSetWinner) {
                            onToggleSetWinner(set, nextSet, toggled, tree);
                          }
                        };
                      } else {
                        winner        = null;
                        badgeDisabled = true;
                        badgeTitle    = 'Cannot determine (last game in match)';
                        handleBadgeClick = (e) => e.stopPropagation();
                      }
                    } else {
                      winner       = nextPoint ? getOutcome(point.pointScore, nextPoint.pointScore) : null;
                      badgeDisabled = !winner || !onTogglePointWinner;
                      badgeTitle    = winner
                        ? `${winner} won — click to toggle`
                        : 'Cannot determine winner';
                      const toggled = winner === 'server' ? 'receiver' : 'server';
                      handleBadgeClick = (e) => {
                        e.stopPropagation();
                        if (winner && nextPoint && onTogglePointWinner) {
                          onTogglePointWinner(point, nextPoint, toggled, game.points);
                        }
                      };
                    }

                    const winnerLabel = winner === 'server' ? 'S' : winner === 'receiver' ? 'R' : '?';
                    const winnerColor = winner === 'server'   ? '#4ade80'
                                      : winner === 'receiver' ? '#fb923c'
                                      : theme.colors.textMuted;

                    return (
                      <div key={`point-${si}-${gi}-${pi}`} style={{ position: 'relative' }}>
                        <PointRow
                          $isActive={isActive}
                          onClick={() => { setOpenMenuKey(null); onPointClick && onPointClick(point); }}
                          title={`Jump to ${ts}`}
                        >
                          <ActiveDot $visible={isActive} />
                          <span>Point {pi + 1}: {point.pointScore}</span>
                          <span style={{ flex: 1 }} />
                          <span style={{
                            fontSize: theme.typography.fontSizes.xs,
                            color: theme.colors.textMuted,
                            paddingLeft: theme.spacing.xs,
                          }}>
                            {ts}
                          </span>
                          <WinnerBadge
                            $color={winnerColor}
                            disabled={badgeDisabled}
                            title={badgeTitle}
                            onClick={handleBadgeClick}
                          >
                            {winnerLabel}
                          </WinnerBadge>
                          <EllipsisBtn
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuKey(menuOpen ? null : menuKey);
                            }}
                            title="Options"
                          >
                            ⋯
                          </EllipsisBtn>
                        </PointRow>
                        {menuOpen && (
                          <PointMenu>
                            <PointMenuItem
                              disabled={!hasPrev}
                              onClick={() => {
                                if (hasPrev && onMergeWithPrevious) {
                                  onMergeWithPrevious(point, game.points[pi - 1], game.points);
                                }
                                setOpenMenuKey(null);
                              }}
                            >
                              Merge with previous point
                            </PointMenuItem>
                            <PointMenuItem
                              title="Split current point at current video time and insert new point (server wins)"
                              onClick={() => {
                                if (onAddPointAfter) {
                                  // game.points[pi + 1] is null for the last point — handler handles both cases
                                  onAddPointAfter(point, game.points[pi + 1] ?? null, game.points);
                                }
                                setOpenMenuKey(null);
                              }}
                            >
                              Add point here
                            </PointMenuItem>
                            <PointMenuItem
                              disabled={!canSplit}
                              title={canSplit ? 'Move all subsequent points in this game into a new game' : 'No subsequent points to split off'}
                              onClick={() => {
                                if (canSplit && onSplitGame) {
                                  onSplitGame(point, game, set.games);
                                }
                                setOpenMenuKey(null);
                              }}
                            >
                              Split remaining into new game
                            </PointMenuItem>
                          </PointMenu>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </TreeScroll>
  );
});

// ---------------------------------------------------------------------------
// ScoringPanel
//
// Props:
//   embedded  — when true, skip the outer PanelContainer/PanelHeader so the
//               component can live inside an existing card.
// ---------------------------------------------------------------------------

// ScoringPanel is React.memo'd so it only re-renders when annotations change
// or the active rally changes — NOT on every video frame. The caller must
// compute activeStartTime and pass it as a primitive prop.
const ScoringPanel = React.memo(({ annotations = [], activeStartTime = null, onSeek, onMergeWithPrevious, onAddPointAfter, onTogglePointWinner, onToggleGameWinner, onToggleSetWinner, onSplitGame, onMergeGameWithPrevious, embedded = false, jumpTarget: jumpTargetProp, onJumpTargetChange }) => {
  const [expandedSets,  setExpandedSets]  = useState(() => new Set());
  const [expandedGames, setExpandedGames] = useState(() => new Set());
  const [jumpTargetInternal, setJumpTargetInternal] = useState('end');

  // Controlled when jumpTargetProp is provided; otherwise use internal state.
  const jumpTarget    = jumpTargetProp !== undefined ? jumpTargetProp : jumpTargetInternal;
  const setJumpTarget = (val) => {
    if (onJumpTargetChange) onJumpTargetChange(val);
    else setJumpTargetInternal(val);
  };

  // Keep a ref to onSeek so handlePointClick doesn't need it as a dependency,
  // preventing ScoringTree from re-rendering every time the parent re-renders.
  const onSeekRef = useRef(onSeek);
  onSeekRef.current = onSeek;

  // Track the last activeStartTime the expansion layout was computed for.
  // We only reset expansion when the active point changes (playback / file load),
  // NOT when annotations are manually edited (which also changes `tree`).
  const lastExpandActiveRef = useRef(undefined);

  // Stable click handler — only changes when jumpTarget changes, not per frame.
  const handlePointClick = useMemo(() => (point) => {
    const time = jumpTarget === 'start' ? point.startTime : point.endTime;
    if (onSeekRef.current) onSeekRef.current(time);
  }, [jumpTarget]);

  // Build tree only when annotations change.
  // activeStartTime is computed outside (in VideoPlayer) and passed as a prop,
  // so ScoringPanel never receives currentTime and avoids per-frame re-renders.
  const tree = useMemo(() => buildScoringTree(annotations), [annotations]);

  // Keep expansion in sync with the active point. Fires on file load AND
  // whenever the active rally changes during playback, but NOT when annotations
  // are edited (which also rebuilds `tree` without changing `activeStartTime`).
  useEffect(() => {
    if (tree.length === 0) return;
    // Skip if the active point hasn't changed — this prevents manual score edits
    // (which rebuild the tree) from collapsing the user's current expansion state.
    if (lastExpandActiveRef.current === activeStartTime) return;
    lastExpandActiveRef.current = activeStartTime;

    const pts = flattenPoints(tree);
    const activePoint = activeStartTime !== null
      ? pts.find((p) => p.startTime === activeStartTime)
      : null;

    // Start with everything collapsed …
    const collapsedSets  = new Set(tree.map((s) => s.setScore));
    const collapsedGames = new Set(
      tree.flatMap((s) => s.games.map((g) => `${s.setScore}::${g.gameScore}`))
    );

    // … then open only the active set and game.
    if (activePoint) {
      collapsedSets.delete(activePoint.setScore);
      collapsedGames.delete(`${activePoint.setScore}::${activePoint.gameScore}`);
    }

    setExpandedSets(collapsedSets);
    setExpandedGames(collapsedGames);
  }, [activeStartTime, tree]);

  const toggleSet = useMemo(() => (s) => {
    const k = s.setScore;
    setExpandedSets((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, []);

  const toggleGame = useMemo(() => (s, g) => {
    const k = `${s.setScore}::${g.gameScore}`;
    setExpandedGames((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, []);

  const toggleBar = (
    <ToggleBar>
      <ToggleLabel>Jump to:</ToggleLabel>
      <ToggleBtn $active={jumpTarget === 'start'} onClick={() => setJumpTarget('start')}>
        Rally start
      </ToggleBtn>
      <ToggleBtn $active={jumpTarget === 'end'} onClick={() => setJumpTarget('end')}>
        Rally end
      </ToggleBtn>
    </ToggleBar>
  );

  const emptyMsg = (
    <EmptyMsg>Load annotations with scoring data to navigate by point.</EmptyMsg>
  );

  const treeContent = tree.length === 0 ? emptyMsg : (
    <ScoringTree
      tree={tree}
      activeStartTime={activeStartTime}
      jumpTarget={jumpTarget}
      onPointClick={handlePointClick}
      onMergeWithPrevious={onMergeWithPrevious}
      onAddPointAfter={onAddPointAfter}
      onTogglePointWinner={onTogglePointWinner}
      onToggleGameWinner={onToggleGameWinner}
      onToggleSetWinner={onToggleSetWinner}
      onSplitGame={onSplitGame}
      onMergeGameWithPrevious={onMergeGameWithPrevious}
      expandedSets={expandedSets}
      expandedGames={expandedGames}
      toggleSet={toggleSet}
      toggleGame={toggleGame}
    />
  );

  if (embedded) {
    return (
      <>
        {toggleBar}
        {treeContent}
      </>
    );
  }

  return (
    <PanelContainer>
      <PanelHeader>Scoring</PanelHeader>
      {toggleBar}
      {treeContent}
    </PanelContainer>
  );
});

export default ScoringPanel;
